import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import { applyColumnOrder, partitionColumns } from './utils';

type Props<T> = {
  columns?: ColumnsType<T>;
  columnOrder: string[] | undefined;
  enabled: boolean;
  setColumnOrder: (order: string[] | undefined) => void;
};

const useReorderableColumns = <T>({
  columns,
  columnOrder,
  enabled,
  setColumnOrder
}: Props<T>) => {
  const partitioned = useMemo(
    () => (columns ? partitionColumns(columns) : null),
    [columns]
  );

  const orderedReorderable = useMemo(() => {
    if (!partitioned) return [];
    if (!enabled) return partitioned.reorderable;

    return applyColumnOrder(partitioned.reorderable, columnOrder);
  }, [partitioned, columnOrder, enabled]);

  const reorderableKeys = useMemo(
    () =>
      orderedReorderable
        .map(col => (col as ColumnType<T>).key)
        .filter((k): k is string | number => k !== undefined && k !== null)
        .map(String),
    [orderedReorderable]
  );

  const orderedColumns = useMemo<ColumnsType<T> | undefined>(() => {
    if (!columns) return columns;
    if (!partitioned) return columns;

    const decorated = enabled
      ? orderedReorderable.map(withColumnKeyAttr)
      : orderedReorderable;

    return [...partitioned.fixedLeft, ...decorated, ...partitioned.fixedRight];
  }, [columns, partitioned, orderedReorderable, enabled]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!enabled) return;

      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = reorderableKeys.indexOf(String(active.id));
      const newIndex = reorderableKeys.indexOf(String(over.id));

      if (oldIndex < 0 || newIndex < 0) return;

      setColumnOrder(arrayMove(reorderableKeys, oldIndex, newIndex));
    },
    [enabled, reorderableKeys, setColumnOrder]
  );

  return {
    orderedColumns,
    reorderableKeys,
    handleDragEnd
  };
};

const withColumnKeyAttr = <T>(col: ColumnsType<T>[number]) => {
  const rawKey = (col as ColumnType<T>).key;

  if (rawKey === undefined || rawKey === null) return col;

  const columnKey = String(rawKey);
  const prevOnHeaderCell = (col as ColumnType<T>).onHeaderCell;

  return {
    ...col,
    onHeaderCell: (
      column: Parameters<NonNullable<ColumnType<T>['onHeaderCell']>>[0]
    ) => ({
      ...(prevOnHeaderCell ? prevOnHeaderCell(column) : {}),
      'data-column-key': columnKey
    })
  };
};

export default useReorderableColumns;
